import { useTheme } from '@emotion/react'
import { EditRounded, SaveRounded } from '@mui/icons-material'
import { Paper, Stack, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import AlertUI from '../../generic/AlertUI'
import { apiGet, apiPut } from '../../generic/Api_Functions'
import GenericFloatingButton from '../../generic/buttons/floatingButtons/GenericFloatingButton'
import { FABRICATION_ORDER_CLUSTER_SEND_SETTING_ENDPOINT } from '../../generic/hooks/useFabricationOrderClusterSendSetting'
import { useAuthorization } from '../../generic/hooks/useAuthorization'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { useToggle } from '../../generic/hooks/useToggle'
import { SmartSelect } from '../../generic/smartSelect'
import { SettingRow, SettingsSection } from './SettingsList'
import useClusterSendSettingControl, { ALIGNMENT_SETTING_ENDPOINT, DEVICE_STATUS_SEND_SETTING_ENDPOINT, ORDER_DATA_SEND_SETTING_ENDPOINT, PRODUCTION_FEEDBACK_SEND_SETTING_ENDPOINT } from './useClusterSendSettingControl'

/**
 * Labels and help of the two cluster switches. Each endpoint answers `{ enabled }` and nothing else
 * -- on purpose, says its own documentation: the wording belongs to the screen, not to the
 * exchange -- so it is owned here and translated like any other visible string.
 */
const CLUSTER_SEND_SETTING_LABEL = 'Send fabrication orders to another node'
const ORDER_DATA_SETTING_LABEL = 'Send order data with the fabrication orders'
const SENDING_SECTION_DESCRIPTION = 'What travels when an order is sent to another node'
// Replaces the description of the sending section while the send is off: with nothing travelling,
// what the settings of that group do is moot, and saying so once beats repeating it per row.
const NOTHING_SENT_NOTE = 'Sending fabrication orders to another node is off, so nothing is sent at all.'
// S69: the user has to be able to learn this BEFORE toggling the order data switch. It lives in the
// description of the section rather than under the row, so the rows stay one line each as the list
// grows -- and it is stated whether the send is on or off, because it qualifies the switch itself.
const NOT_RETROACTIVE_NOTE = 'The change is not retroactive: orders already sent keep the commercial link they arrived with.'
// S126 (historia 108). ⚠️ RN41″: el ajuste gobierna lo que este nodo EMITE. Apagado, el nodo sigue
// RECIBIENDO y aplicando lo que le llega, así que ni la etiqueta ni la descripción pueden leerse
// como "desactivar la sincronización".
// ⚠️ Historia 132 (S39): this switch now governs BOTH directions of the alignment -- the changes
// made here travelling out, and the quantity produced elsewhere coming back -- and the wording the
// user reads says so. The key below is deliberately NOT renamed: renaming it would mean touching
// every locale AND this file at once, and a miss falls back to rendering the raw literal, which is
// the silent failure that bit in the 108. So what changed is its VALUE in the eight locale files,
// which is why the English value of this key no longer equals the key itself -- the one place in
// this screen where the repo's "the key is the English text" convention is knowingly broken.
// Pinned by `local/__tests__/story132I18nCatalog.test.js`.
const ALIGNMENT_SETTING_LABEL = 'Keep sent fabrication orders aligned'
const ALIGNMENT_SECTION_DESCRIPTION = 'Whether the changes made here travel to the orders already sent to another node. With this off, this node still receives and applies what arrives.'

// Historia 132: what this node reports to another one about its own shop floor. Its OWN section,
// not two more rows of the sending one: these switches are not about fabrication orders, and the
// sending section tells the user "nothing is sent at all" when the order send is off -- which would
// be false with these two on, since they are independent of it.
const DEVICE_STATUS_SETTING_LABEL = 'Send machine states to another node'
const PRODUCTION_FEEDBACK_SETTING_LABEL = 'Send production of parts and jobs to another node'
const REPORTING_SECTION_HEADING = 'Machine and production reporting'

const ClusterSettings = (props) => {
  const [AlertElement, showAlert] = AlertUI()
  const [selectableClusters, setSelectableClusters] = useState([])
  const [clusters, setClusters] = useState([])
  // S54 (historia 107): the two switches of the plant-cell cluster. They are INDEPENDENT -- orders
  // can be sent without their commercial data -- and each one lives on a route of its own, so they
  // are read and written separately and neither one ever changes the value of the other.
  const clusterSend = useClusterSendSettingControl(FABRICATION_ORDER_CLUSTER_SEND_SETTING_ENDPOINT)
  const orderDataSend = useClusterSendSettingControl(ORDER_DATA_SEND_SETTING_ENDPOINT)
  const alignment = useClusterSendSettingControl(ALIGNMENT_SETTING_ENDPOINT)
  // Historia 132: the two switches of what this node reports back. Same family, same contract, and
  // independent of every switch above and of each other -- neither is ever flipped or locked on
  // behalf of the other.
  const deviceStatusSend = useClusterSendSettingControl(DEVICE_STATUS_SEND_SETTING_ENDPOINT)
  const productionFeedbackSend = useClusterSendSettingControl(PRODUCTION_FEEDBACK_SEND_SETTING_ENDPOINT)
  const { t_ } = useSmartTranslation()
  const isUserAuthorized = useAuthorization()
  const [editing, setEditing] = useToggle()
  const theme = useTheme()

  // Writing a switch needs the `SETTINGS` authority in Sun; reading it needs none, deliberately.
  // Disabling the control is more honest than letting the user toggle it and meet a 403.
  const canEditSettings = isUserAuthorized('SETTINGS')
  // The screen has ONE edit mode, and every control obeys it: a screen where one field is live
  // while the rest is read-only reads as a defect. A switch still writes the moment it is
  // toggled -- it has no Save of its own -- but it can only be toggled while editing.
  const settingEditable = (control) => editing && canEditSettings && control.available

  useEffect(() => {
    const getSelectableClusters = async () => {
      const response = await apiGet('cluster/getNodeList')
      setSelectableClusters(response)
      const userFilteredClusters = await apiGet('cluster/getUserNodes')
      setClusters(userFilteredClusters)
    }
    getSelectableClusters()
  }, [])

  const onChange = (newSelectedValues) => {
    let newValues = []
    newSelectedValues.forEach((value) => {
      const newValue = selectableClusters.find(elem => elem.name === value)
      newValues = newValues.concat([newValue])
    })
    setClusters(newValues)
  }

  const updateUserClusters = async () => {
    await apiPut('updateUserClusters', clusters.map((item) => item.id))
      .then((response) => {
        showAlert({ severity: 'success', title: t_('Successfully updated') })
      })
      .catch((e) => {
        showAlert({
          title: '',
          message: e.message,
          severity: 'error'
        })
      })
  }

  /**
   * Writes a switch the moment the user changes it: these controls have no Save of their own, and
   * tying them to the one of the node filter would make a request the user did not ask for.
   *
   * A refusal is reported and leaves the control where it was, because the installation did not
   * change: the 400 of these routes means the setting does not exist there and NOTHING was written.
   *
   * @param {Object} control Switch being written
   * @param {string} selected YES/NO chosen by the user
   * @returns {Promise<void>} Resolves once the switch reflects the state of the server
   */
  const onSettingChange = async (control, selected) => {
    const result = await control.change(selected === t_('YES'))
    if (!result.ok) {
      showAlert({
        title: t_('Error saving the cluster settings'),
        message: result.message,
        severity: 'error'
      })
    }
  }

  // A settings LIST rather than a form: sections group the settings by what they affect, and each
  // setting is one row. The cluster area is expected to gain more switches, and this way adding one
  // is appending a `<SettingRow>` with no layout to rethink.
  //
  // With the send off nothing travels at all, and that is said ONCE in the description of its
  // section instead of under the row: it is context for the whole group, and repeating it per row
  // is what made the previous version noisy.
  return (
    <Paper elevation={0} variant='outlined' sx={{ width: '100%', maxWidth: '56rem', borderRadius: 2 }}>
      <Stack gap={3} sx={{ p: 3 }}>
        {AlertElement}

        <Stack direction='row' alignItems='center' justifyContent='space-between' gap={2}>
          <Typography variant='h6'>{t_('Cluster settings')}</Typography>
          <GenericFloatingButton
            label={editing ? t_('Save') : t_('Edit')}
            icon={editing ? <SaveRounded /> : <EditRounded />}
            color={theme.palette.primary.main}
            opacity={1}
            onClick={() => {
              if (editing) {
                updateUserClusters()
              }
              setEditing()
            }}
            sx={{ minWidth: '8rem' }}
          />
        </Stack>

        <SettingsSection
          heading={t_('Visibility')}
          description={t_('What this node shows from the rest of the cluster')}
        >
          <SettingRow label={t_('Filter resources created by nodes')} controlWidth='22rem'>
            <SmartSelect
              selectableOptions={selectableClusters.map((item) => item.name)}
              value={clusters.map((item) => item.name)}
              multiple
              onChange={(newValues) => onChange(newValues)}
              label={t_('Filter resources created by nodes')}
              disabled={!editing}
              limitTags={3}
              size='small'
            />
          </SettingRow>
        </SettingsSection>

        <SettingsSection
          heading={t_('Fabrication order sending')}
          description={(clusterSend.enabled ? t_(SENDING_SECTION_DESCRIPTION) : t_(NOTHING_SENT_NOTE)) +
            ' ' + t_(NOT_RETROACTIVE_NOTE)}
        >
          <SettingRow label={t_(CLUSTER_SEND_SETTING_LABEL)}>
            <SmartSelect
              selectableOptions={[t_('YES'), t_('NO')]}
              value={clusterSend.enabled ? t_('YES') : t_('NO')}
              onChange={(selected) => onSettingChange(clusterSend, selected)}
              label={t_(CLUSTER_SEND_SETTING_LABEL)}
              disabled={!settingEditable(clusterSend)}
              size='small'
            />
          </SettingRow>

          {/*
            Independent of the switch above: orders can be sent without their commercial data, so
            this one is never flipped or locked on its behalf.
          */}
          <SettingRow label={t_(ORDER_DATA_SETTING_LABEL)}>
            <SmartSelect
              selectableOptions={[t_('YES'), t_('NO')]}
              value={orderDataSend.enabled ? t_('YES') : t_('NO')}
              onChange={(selected) => onSettingChange(orderDataSend, selected)}
              label={t_(ORDER_DATA_SETTING_LABEL)}
              disabled={!settingEditable(orderDataSend)}
              size='small'
            />
          </SettingRow>
        </SettingsSection>

        {/*
          S126 (historia 108): sección PROPIA, no una fila más de la de envío. Es otra pregunta —
          enviar es "¿puedo mandar OFs?", alinear es "¿se mantienen sincronizadas después?" — y la
          nota de "con el envío apagado no viaja nada" de la sección anterior no le aplica.
        */}
        <SettingsSection
          heading={t_('Fabrication order alignment')}
          description={t_(ALIGNMENT_SECTION_DESCRIPTION)}
        >
          <SettingRow label={t_(ALIGNMENT_SETTING_LABEL)}>
            <SmartSelect
              selectableOptions={[t_('YES'), t_('NO')]}
              value={alignment.enabled ? t_('YES') : t_('NO')}
              onChange={(selected) => onSettingChange(alignment, selected)}
              label={t_(ALIGNMENT_SETTING_LABEL)}
              disabled={!settingEditable(alignment)}
              size='small'
            />
          </SettingRow>
        </SettingsSection>

        {/*
          Historia 132 (S38): las dos filas nuevas, DESPUÉS de las tres existentes y en sección
          propia. Sin descripción a propósito: el contrato de la historia cerraba el lado jupiter en
          tres textos traducidos, el PO aprobó UNO más para el encabezado (2026-09-14), y una
          descripción sería un quinto. Las filas se explican solas.
        */}
        <SettingsSection heading={t_(REPORTING_SECTION_HEADING)}>
          <SettingRow label={t_(DEVICE_STATUS_SETTING_LABEL)}>
            <SmartSelect
              selectableOptions={[t_('YES'), t_('NO')]}
              value={deviceStatusSend.enabled ? t_('YES') : t_('NO')}
              onChange={(selected) => onSettingChange(deviceStatusSend, selected)}
              label={t_(DEVICE_STATUS_SETTING_LABEL)}
              disabled={!settingEditable(deviceStatusSend)}
              size='small'
            />
          </SettingRow>

          {/*
            Independiente de la de arriba: la dependencia de negocio —con los estados de máquina
            apagados el histórico por pieza no viaja— la aplica el backend, y pintarla aquí
            deshabilitando la fila le diría al usuario que el ajuste no está disponible, que es otra
            cosa.
          */}
          <SettingRow label={t_(PRODUCTION_FEEDBACK_SETTING_LABEL)}>
            <SmartSelect
              selectableOptions={[t_('YES'), t_('NO')]}
              value={productionFeedbackSend.enabled ? t_('YES') : t_('NO')}
              onChange={(selected) => onSettingChange(productionFeedbackSend, selected)}
              label={t_(PRODUCTION_FEEDBACK_SETTING_LABEL)}
              disabled={!settingEditable(productionFeedbackSend)}
              size='small'
            />
          </SettingRow>
        </SettingsSection>
      </Stack>
    </Paper>
  )
}

export default ClusterSettings
